import { getReportFooterText } from '../reportFooter';

export interface InventoryMovementRow {
  lin: string | null;
  nsn: string;
  nomenclature: string;
  reqQty: number;
  reqCost: number | null;
  disposalQty: number;
  disposalCost: number | null;
  qtyDifference: number;
  costDifference: number | null;
}

export interface InventoryMovementTemplateOptions {
  generatedAt: Date;
  header: {
    fromDate: string | null;
    toDate: string | null;
  };
  rows: InventoryMovementRow[];
}

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const sanitizeText = (value: string | null | undefined): string => {
  const trimmed = value?.toString().trim();
  if (!trimmed) {
    return '—';
  }
  return escapeHtml(trimmed);
};

const formatCurrency = (value: number | null | undefined): string => {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return '—';
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 2,
  }).format(value);
};

const formatDate = (value: string | null | undefined): string => {
  if (!value) {
    return '—';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return sanitizeText(value);
  }
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);
};

const formatDateTime = (value: Date): string =>
  new Intl.DateTimeFormat('en-US', {
    dateStyle: 'long',
    timeStyle: 'short',
  }).format(value);

const buildRowsTable = (rows: InventoryMovementRow[]): string => {
  if (rows.length === 0) {
    return `
      <tr>
        <td colspan="9" class="empty-row">
          No requisition or disposal activity matched the selected filters.
        </td>
      </tr>
    `;
  }

  return rows
    .map(
      (row) => `
        <tr>
          <td>${sanitizeText(row.lin)}</td>
          <td>${sanitizeText(row.nsn)}</td>
          <td>${sanitizeText(row.nomenclature)}</td>
          <td class="numeric">${row.reqQty}</td>
          <td class="numeric">${formatCurrency(row.reqCost)}</td>
          <td class="numeric">${row.disposalQty}</td>
          <td class="numeric">${formatCurrency(row.disposalCost)}</td>
          <td class="numeric">${row.qtyDifference}</td>
          <td class="numeric">${formatCurrency(row.costDifference)}</td>
        </tr>
      `,
    )
    .join('');
};

export const buildInventoryMovementReportHtml = ({
  generatedAt,
  header,
  rows,
}: InventoryMovementTemplateOptions): string => {
  const generatedLabel = formatDateTime(generatedAt);
  const fromDateLabel = header.fromDate ? formatDate(header.fromDate) : '—';
  const toDateLabel = header.toDate ? formatDate(header.toDate) : '—';
  const rowsTable = buildRowsTable(rows);

  return `<!DOCTYPE html>
<html lang="en">
  <head>
    <meta charset="utf-8" />
    <title>Inventory Movement Report</title>
    <style>
      :root {
        color-scheme: light;
      }

      * {
        box-sizing: border-box;
      }

      body {
        margin: 16px;
        font-family: 'Roboto', 'Helvetica Neue', Arial, sans-serif;
        color: #15263c;
        background: #ffffff;
      }

      header {
        margin-bottom: 16px;
      }

      .title {
        font-size: 24px;
        font-weight: 700;
        margin: 0;
        text-align: center;
      }

      .meta-row {
        display: flex;
        flex-wrap: wrap;
        gap: 16px;
        margin-top: 12px;
      }

      .meta-block {
        min-width: 220px;
      }

      .meta-label {
        font-size: 12px;
        font-weight: 600;
        letter-spacing: 0.06em;
        text-transform: uppercase;
        color: #5c6f85;
        margin-bottom: 2px;
      }

      .meta-value {
        font-size: 14px;
        color: #15263c;
      }

      table {
        width: 100%;
        border-collapse: collapse;
        margin-top: 16px;
        border: 1px solid #d7e3f4;
      }

      thead th {
        background: #e9f1ff;
        text-align: left;
        font-size: 12px;
        letter-spacing: 0.05em;
        text-transform: uppercase;
        padding: 10px 12px;
        color: #4a5c72;
        border-bottom: 1px solid #d7e3f4;
      }

      tbody td {
        padding: 10px 12px;
        border-bottom: 1px solid #edf3fc;
        font-size: 14px;
        color: #1f2f45;
      }

      tbody tr:last-child td {
        border-bottom: none;
      }

      tbody tr:nth-child(even) td {
        background: #f9fbff;
      }

      .numeric {
        text-align: right;
        font-variant-numeric: tabular-nums;
      }

      .empty-row {
        text-align: center;
        font-style: italic;
        color: #6b7c92;
        padding: 24px 12px;
      }

      footer {
        margin-top: 24px;
        font-size: 12px;
        color: #5c6f85;
        text-align: center;
      }

      @media print {
        body {
          margin: 8mm;
        }
      }
    </style>
  </head>
  <body>
    <header>
      <h1 class="title">Inventory Movement Report</h1>
      <div class="meta-row">
        <div class="meta-block">
          <div class="meta-label">From</div>
          <div class="meta-value">${fromDateLabel}</div>
        </div>
        <div class="meta-block">
          <div class="meta-label">To</div>
          <div class="meta-value">${toDateLabel}</div>
        </div>
      </div>
    </header>

    <section>
      <table>
        <thead>
          <tr>
            <th>LIN</th>
            <th>NSN</th>
            <th>Nomenclature</th>
            <th>Req Qty</th>
            <th>Req Cost</th>
            <th>Disposal Qty</th>
            <th>Disposal Cost</th>
            <th>Qty Difference</th>
            <th>Cost Difference</th>
          </tr>
        </thead>
        <tbody>
          ${rowsTable}
        </tbody>
      </table>
    </section>

    <footer>
      Generated ${escapeHtml(generatedLabel)} · ${getReportFooterText()}
    </footer>
  </body>
</html>`;
};
